/**
 * TrendChart — real React charts (line/area + stacked bars) replacing the old
 * dead string-injected SVGs. Hover anywhere: crosshair + tooltip with the
 * month's values. The current (partial) month renders muted with a "partial"
 * tag so every chart stops ending in a fake cliff.
 */

import { useMemo, useRef, useState } from "react";
import { moneyFmt } from "./cards.js";
import { ChartTypeMenu, useChartPref, rollingAverage, rollingWindow } from "./ChartTypeMenu.js";

export interface TrendPoint {
  label: string; // "YYYY-MM"
  /**
   * One value (line mode), two stacked values (stacked-bars: a bottom, b top),
   * or two side-by-side values (grouped-bars: a vs b — e.g. new vs canceled).
   */
  a: number;
  b?: number;
}

/** The chart-type menu (founder 2026-06-13: "a menu of chart types per card").
 *  line = crisp line · area = line + fill · stacked/grouped bars · table =
 *  the numbers · number = the total collapsed to one figure. Pass the subset
 *  that's HONEST for the card's data (Spec §18.2). */
export type TrendKind = "line" | "area" | "step" | "stacked-bars" | "grouped-bars" | "table" | "number";

const W = 600;
const H = 220;
const PAD = { l: 46, r: 10, t: 10, b: 26 };
const IW = W - PAD.l - PAD.r;
const IH = H - PAD.t - PAD.b;

/** The bucket containing "now" renders as partial (dashed/faded). Works for
 *  month keys (YYYY-MM) and day/week keys (YYYY-MM-DD — a week is keyed by
 *  its start date, so "within the last 7 days" covers it). */
function isCurrentMonth(label: string): boolean {
  const now = new Date();
  if (/^\d{4}-\d{2}$/.test(label)) {
    return label === `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
  }
  const d = new Date(`${label}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return false;
  const age = now.getTime() - d.getTime();
  return age >= 0 && age < 7 * 86_400_000;
}

/** Day and week buckets share YYYY-MM-DD keys. Weekly series keep the same
 * weekday and roughly seven-day spacing; daily series do not. Only today's
 * daily bucket is partial, while the current weekly bucket stays partial. */
function isPartialBucket(label: string, points: TrendPoint[]): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(label)) return isCurrentMonth(label);
  const dates = points
    .map((point) => new Date(`${point.label}T00:00:00Z`))
    .filter((date) => !Number.isNaN(date.getTime()));
  const weekly = dates.length >= 2 && dates.every((date) => date.getUTCDay() === dates[0]?.getUTCDay())
    && dates.slice(1).every((date, index) => date.getTime() - dates[index]!.getTime() >= 6 * 86_400_000);
  if (weekly) return isCurrentMonth(label);
  const now = new Date();
  return label === `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}-${String(now.getUTCDate()).padStart(2, "0")}`;
}

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-03" → "Mar 2026" · "2026-03-04" → "Mar 4" — readable axis labels. */
function prettyMonth(label: string): string {
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(label);
  if (day) return `${MONTH_NAMES[Number(day[2]) - 1]} ${Number(day[3])}`;
  const m = /^(\d{4})-(\d{2})$/.exec(label);
  if (!m) return label;
  return `${MONTH_NAMES[Number(m[2]) - 1]} ${m[1]}`;
}

export function TrendChart({
  points,
  kind: kindProp,
  seriesBColor = "var(--accent-2)",
  switchKinds,
  chartKey,
  money = true,
  seriesNames,
  showValues = false,
  percent = false,
  lineOverlay,
  rollingAvg,
  valueFmt,
  pace = false,
  loading = false,
}: {
  points: TrendPoint[];
  kind: TrendKind;
  /** Persist the chart-type + rolling-avg choice under this key, shared with
   *  the card's drilldown so they stay in sync and survive reloads. */
  chartKey?: string;
  /** Series-B bar color (grouped mode). Defaults to the neutral second accent
   *  — pass var(--bad) only when B genuinely means "bad" (losses, churn). */
  seriesBColor?: string;
  /** Render a small kind-toggle (top-right) letting the user flip chart type.
   *  2026-06-12 card audit: "give them the option of changing the chart type
   *  … add that to all of them that would make sense" — pass the kinds that
   *  make sense for THIS data; omit for fixed charts. */
  switchKinds?: TrendKind[];
  money?: boolean;
  /** Two-series legend names: [a, b]. */
  seriesNames?: [string, string];
  /** Print each bar's value above it (DOMO-style) — single-series bars only. */
  showValues?: boolean;
  /** Format values as percentages (money is ignored). */
  percent?: boolean;
  /** Percent series (0–100) drawn as a line OVER bars on its own scale —
   *  the combo-chart pattern (volume bars + rate line). Align with points. */
  lineOverlay?: { values: Array<number | null>; label: string };
  /** Force the rolling-average overlay on/off (the drill drives its own toggle
   *  rather than the per-card menu). When undefined, the menu/pref decides. */
  rollingAvg?: boolean;
  /** Full value formatter (axis, tooltip, table, column labels) — e.g. "8.10×"
   *  for a ratio. Wins over money/percent. */
  valueFmt?: (n: number) => string;
  /** Show a "this month so far · on pace for ~X vs last month" caption when the
   *  last bucket is the current partial month (founder 2026-07-07: the partial
   *  month's drop is confusing — mark it AND say where it's pacing). Opt-in, and
   *  only meaningful for a single-series VOLUME series (revenue/orders/units) —
   *  never a rate, where a linear run-rate projection would mislead. */
  pace?: boolean;
  /** The selected period is still loading. Keep the card honest instead of
   *  treating the temporary lack of points as a real empty result. */
  loading?: boolean;
}): React.ReactElement {
  const [hover, setHover] = useState<number | null>(null);
  // Switcher state — persisted per card key (shared with the drilldown) when a
  // chartKey is given; otherwise in-memory. User choice overrides the default.
  const pref = useChartPref(chartKey, kindProp);
  const hasSwitcher = !!switchKinds && switchKinds.length >= 2;
  const rawKind = (hasSwitcher ? pref.type : kindProp) as TrendKind;
  const setKind = pref.setType;
  // A line/area/step needs >=2 FULL (non-partial) points to draw a visible
  // segment. If a short monthly series cannot support one, show Column in BOTH
  // the drawing and the menu. Never say "Line" while silently rendering bars.
  const isLineType = rawKind === "line" || rawKind === "area" || rawKind === "step";
  const fullCount = points.filter((p) => !isPartialBucket(p.label, points)).length;
  const kind = isLineType && fullCount < 2 ? "stacked-bars" : rawKind;
  const svgRef = useRef<SVGSVGElement>(null);

  const fmt = (v: number): string =>
    valueFmt ? valueFmt(v) : percent ? `${v.toFixed(1)}%` : money ? moneyFmt(v) : Math.round(v).toLocaleString();

  // Pace: when the last bucket is the current, still-filling month, project its
  // run-rate to a full-month estimate and compare to the prior full month — so the
  // partial dip reads as "we're only N days in", not "sales fell off a cliff".
  const paceInfo = ((): { soFar: number; projected: number; deltaPct: number | null; curLabel: string; priorLabel: string } | null => {
    if (!pace || percent || points.length < 2) return null;
    if (!points.every((p) => p.b === undefined)) return null; // single-series only
    const last = points[points.length - 1];
    const prior = points[points.length - 2];
    if (!last || !prior || !/^\d{4}-\d{2}$/.test(last.label) || !isCurrentMonth(last.label)) return null;
    const now = new Date();
    const daysInMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0)).getUTCDate();
    const elapsedFrac = Math.min(1, Math.max(0.06, now.getUTCDate() / daysInMonth));
    if (elapsedFrac >= 0.995) return null; // month is effectively complete — no projection
    const projected = last.a / elapsedFrac;
    const deltaPct = prior.a > 0 ? (projected - prior.a) / prior.a : null;
    return { soFar: last.a, projected, deltaPct, curLabel: prettyMonth(last.label), priorLabel: prettyMonth(prior.label) };
  })();

  const max = useMemo(
    () =>
      Math.max(
        1,
        // Only STACKED bars sum the two series; grouped bars (side by side) and
        // line/area/step (two separate lines) scale to the bigger of the two.
        ...points.map((p) => (kind === "stacked-bars" ? p.a + (p.b ?? 0) : Math.max(p.a, p.b ?? 0))),
      ) * 1.08,
    [points, kind],
  );
  const x = (i: number): number =>
    points.length === 1 ? PAD.l + IW / 2 : PAD.l + (i / (points.length - 1)) * IW;
  const xBar = (i: number): number => PAD.l + (i + 0.5) * (IW / points.length);
  const y = (v: number): number => PAD.t + IH - (v / max) * IH;

  if (loading) {
    return <div role="status" style={{ color: "var(--faint)", padding: "18px 0" }}>Loading…</div>;
  }

  if (points.length === 0) {
    return <div style={{ color: "var(--faint)", padding: "18px 0" }}>No data yet.</div>;
  }

  const isLine = kind === "line" || kind === "area" || kind === "step";
  const twoSeries = points.some((p) => p.b !== undefined);
  // Single-series bars read as a "Column" chart; with two series it's "Stacked".
  const KIND_LABELS: Record<TrendKind, string> = { line: "Line", area: "Area", step: "Step line", "stacked-bars": twoSeries ? "Stacked bars" : "Column", "grouped-bars": "Grouped bars", table: "Table", number: "Number" };
  // Rolling-average trend line applies to any drawn time chart — single OR two
  // series (founder 2026-07-07: "look through all cards, people love those"). For
  // two series we draw a trend line for each.
  const avgApplies = kind === "line" || kind === "area" || kind === "step" || kind === "stacked-bars" || kind === "grouped-bars";
  const availableKinds = (() => {
    if (fullCount >= 2 || !switchKinds) return switchKinds;
    const nonLine = switchKinds.filter((candidate) => candidate !== "line" && candidate !== "area" && candidate !== "step");
    return nonLine.includes("stacked-bars") ? nonLine : (["stacked-bars", ...nonLine] as TrendKind[]);
  })();
  const switcher = hasSwitcher && availableKinds && availableKinds.length >= 2 ? (
    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
      <ChartTypeMenu value={kind} options={availableKinds.map((k) => ({ value: k, label: KIND_LABELS[k] }))}
        onChange={(k) => setKind(k)} avg={pref.avg} onToggleAvg={pref.setAvg} avgApplies={avgApplies} />
    </div>
  ) : null;
  // Rolling-average overlay — one trend line per series. Aligns to the data
  // x-position: line/area/step use x(i), column bars use xBar(i).
  const avgOn = rollingAvg ?? pref.avg;
  const maPaths = ((): Array<{ d: string; color: string }> => {
    if (!avgOn || !avgApplies) return [];
    const mx = isLine ? x : xBar;
    const win = rollingWindow(points.map((p) => p.label));
    const build = (vals: number[], color: string): { d: string; color: string } | null => {
      const ma = rollingAverage(vals, win);
      const segs = ma.map((v, i) => (v === null ? null : `${mx(i).toFixed(1)},${y(v).toFixed(1)}`)).filter((s): s is string => s !== null);
      return segs.length >= 2 ? { d: `M${segs.join(" L")}`, color } : null;
    };
    const out: Array<{ d: string; color: string }> = [];
    const a = build(points.map((p) => p.a), "var(--accent-2)");
    if (a) out.push(a);
    if (twoSeries) {
      const b = build(points.map((p) => p.b ?? 0), seriesBColor ?? "var(--c2)");
      if (b) out.push(b);
    }
    return out;
  })();

  // Non-SVG views: the same data as a table or a single collapsed number.
  if (kind === "table") return <div>{switcher}<TrendTable points={points} fmt={fmt} seriesNames={seriesNames} /></div>;
  if (kind === "number") return <div>{switcher}<TrendNumber points={points} fmt={fmt} seriesNames={seriesNames} /></div>;

  const onMove = (e: React.MouseEvent<SVGSVGElement>): void => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const px = ((e.clientX - rect.left) / rect.width) * W;
    const idx =
      isLine
        ? Math.round(((px - PAD.l) / IW) * (points.length - 1))
        : Math.floor((px - PAD.l) / (IW / points.length));
    setHover(Math.max(0, Math.min(points.length - 1, idx)));
  };

  const hovered = hover === null ? null : points[hover] ?? null;
  const gridVals = [0, max / 2, max];
  const barW = (IW / points.length) * 0.62;

  return (
    <div style={{ position: "relative" }}>
      {switcher}
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
        style={{ display: "block", cursor: "crosshair" }}
      >
        {gridVals.map((v, i) => (
          <g key={i}>
            <line x1={PAD.l} y1={y(v)} x2={W - PAD.r} y2={y(v)} stroke="var(--grid)" />
            <text x={4} y={y(v) + 3} fill="var(--body-faint)" fontSize={10}>
              {fmt(v)}
            </text>
          </g>
        ))}

        {isLine ? (
          <>
            {/* Two-series line/area/step: draw the SECOND series (e.g. Canceled) as
                its own line too — it was silently dropped before (founder 2026-07-06:
                "line/all shows only new, no canceled"). Drawn first so series A is on top. */}
            {twoSeries && <LineSeries points={points} x={x} y={y} step={kind === "step"} pick={(p) => p.b ?? 0} color={seriesBColor} />}
            <LineSeries points={points} x={x} y={y} filled={kind === "area" && !twoSeries} step={kind === "step"} />
          </>
        ) : kind === "grouped-bars" ? (
          points.map((p, i) => {
            const partial = isPartialBucket(p.label, points);
            const cx = xBar(i);
            const half = barW / 2;
            return (
              <g key={p.label} opacity={partial ? 0.45 : 1}>
                <rect x={cx - half} y={y(p.a)} width={half - 1} height={PAD.t + IH - y(p.a)} rx={2} fill="var(--accent)" />
                <rect x={cx + 1} y={y(p.b ?? 0)} width={half - 1} height={PAD.t + IH - y(p.b ?? 0)} rx={2} fill={seriesBColor} />
              </g>
            );
          })
        ) : (
          points.map((p, i) => {
            const partial = isPartialBucket(p.label, points);
            const cx = xBar(i);
            const ya = y(p.a);
            const yTop = y(p.a + (p.b ?? 0));
            return (
              <g key={p.label} opacity={partial ? 0.45 : 1}>
                <rect x={cx - barW / 2} y={ya} width={barW} height={PAD.t + IH - ya} rx={2} fill="var(--accent)" />
                {p.b !== undefined && (
                  <rect x={cx - barW / 2} y={yTop} width={barW} height={ya - yTop} rx={2} fill="var(--accent-2)" />
                )}
                {showValues && p.b === undefined && points.length <= 14 && (
                  <text x={cx} y={ya - 5} textAnchor="middle" fill="var(--body)" fontSize={9.5} className="dd-num">
                    {fmt(p.a)}
                  </text>
                )}
              </g>
            );
          })
        )}

        {/* Rolling-average trend overlay (founder 2026-06-13: "rolling averages
            … a clear indicator of trends"). */}
        {maPaths.length > 0 && (
          <g>
            {maPaths.map((m, i) => (
              <path key={i} d={m.d} fill="none" stroke={m.color} strokeWidth={2.2} strokeDasharray="5 3" strokeLinejoin="round" />
            ))}
            <text x={W - PAD.r} y={PAD.t + 9} textAnchor="end" fill="var(--accent-2)" fontSize={9} fontWeight={700}>
              {rollingWindow(points.map((p) => p.label))}-pt avg
            </text>
          </g>
        )}

        {/* Rate line overlay (combo chart): percent series on its own 0–100%
            scale over the bars. */}
        {lineOverlay && (() => {
          const oy = (v: number): number => PAD.t + IH - (Math.max(0, Math.min(100, v)) / 100) * IH;
          const segments: Array<Array<[number, number]>> = [];
          let segment: Array<[number, number]> = [];
          lineOverlay.values.forEach((v, i) => {
            if (v === null) {
              if (segment.length > 0) segments.push(segment);
              segment = [];
            } else {
              segment.push([xBar(i), oy(v)]);
            }
          });
          if (segment.length > 0) segments.push(segment);
          const pts = segments.flat();
          if (pts.length === 0) return null;
          return (
            <g>
              {segments.filter((s) => s.length >= 2).map((s, segmentIndex) => {
                const d = s.map((q, i) => `${i ? "L" : "M"}${q[0].toFixed(1)},${q[1].toFixed(1)}`).join(" ");
                return <path key={segmentIndex} d={d} fill="none" stroke="var(--c2)" strokeWidth={2} strokeLinejoin="round" />;
              })}
              {pts.map((q, i) => <circle key={i} cx={q[0]} cy={q[1]} r={2.4} fill="var(--c2)" />)}
            </g>
          );
        })()}

        {/* X labels — cap at ~12 evenly spaced so a 5-year drill stays legible */}
        {points.map((p, i) => {
          const step = points.length <= 7 ? 1 : Math.max(1, Math.ceil(points.length / 12));
          return i % step === 0 ? (
            <text key={p.label} x={isLine ? x(i) : xBar(i)} y={H - 9} textAnchor="middle" fill="var(--body-faint)" fontSize={9}>
              {prettyMonth(p.label)}
            </text>
          ) : null;
        })}

        {/* Hover crosshair */}
        {hover !== null && (
          <line
            x1={isLine ? x(hover) : xBar(hover)}
            y1={PAD.t}
            x2={isLine ? x(hover) : xBar(hover)}
            y2={PAD.t + IH}
            stroke="var(--body-faint)"
            strokeDasharray="3 3"
          />
        )}
      </svg>

      {hovered && hover !== null && (
        <div
          style={{
            position: "absolute",
            left: `${(((isLine ? x(hover) : xBar(hover)) / W) * 100).toFixed(1)}%`,
            top: 0,
            transform: `translateX(${hover > points.length / 2 ? "-104%" : "6px"})`,
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            boxShadow: "var(--shadow-pop)",
            padding: "8px 11px",
            fontSize: 12,
            pointerEvents: "none",
            whiteSpace: "nowrap",
            zIndex: 5,
          }}
        >
          <div style={{ color: "var(--text)", fontWeight: 600, marginBottom: 3 }}>
            {prettyMonth(hovered.label)}
            {isPartialBucket(hovered.label, points) && <span style={{ color: "var(--c2)", fontWeight: 600 }}> · partial</span>}
          </div>
          {hover !== null && lineOverlay && lineOverlay.values[hover] !== null && lineOverlay.values[hover] !== undefined && (
            <div style={{ color: "var(--c2)", fontSize: 11.5, marginBottom: 2 }}>
              {lineOverlay.label}: <b className="dd-num">{lineOverlay.values[hover]!.toFixed(1)}%</b>
            </div>
          )}
          {hovered.b === undefined ? (
            <div className="dd-num" style={{ color: "var(--num)", fontWeight: 700 }}>{fmt(hovered.a)}</div>
          ) : (
            <>
              <div style={{ color: "var(--body)" }}>
                <i style={dot("var(--accent)")} />{seriesNames?.[0] ?? "A"}: <b className="dd-num" style={{ color: "var(--num)" }}>{fmt(hovered.a)}</b>
              </div>
              <div style={{ color: "var(--body)" }}>
                <i style={dot(kind === "grouped-bars" ? seriesBColor : "var(--accent-2)")} />{seriesNames?.[1] ?? "B"}: <b className="dd-num" style={{ color: "var(--num)" }}>{fmt(hovered.b)}</b>
              </div>
              <div style={{ color: "var(--body-faint)", marginTop: 2 }}>
                {kind === "grouped-bars" ? (
                  <>Net: <span className="dd-num" style={{ color: hovered.a - hovered.b >= 0 ? "var(--good)" : "var(--bad)", fontWeight: 600 }}>{hovered.a - hovered.b >= 0 ? "+" : "−"}{fmt(Math.abs(hovered.a - hovered.b))}</span></>
                ) : (
                  <>Total: <span className="dd-num">{fmt(hovered.a + hovered.b)}</span></>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {paceInfo && (
        <div style={{ marginTop: 6, fontSize: 11.5, color: "var(--body-faint)", display: "flex", flexWrap: "wrap", gap: 6, alignItems: "baseline" }}>
          <span>{paceInfo.curLabel} so far <b className="dd-num" style={{ color: "var(--body)" }}>{fmt(paceInfo.soFar)}</b></span>
          <span>· on pace for <b className="dd-num" style={{ color: "var(--num)" }}>~{fmt(paceInfo.projected)}</b></span>
          {paceInfo.deltaPct !== null && (
            <span className="dd-num" style={{ fontWeight: 600, color: paceInfo.deltaPct >= 0 ? "var(--good)" : "var(--bad)" }}>
              {paceInfo.deltaPct >= 0 ? "▲" : "▼"} {Math.abs(paceInfo.deltaPct * 100).toFixed(0)}% vs {paceInfo.priorLabel}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/** Line, optionally with an area fill ("area" kind) or stepped ("step" kind);
 *  the segment into a partial current month is dashed. */
function LineSeries({ points, x, y, filled = false, step = false, pick = (p) => p.a, color = "var(--accent)" }: { points: TrendPoint[]; x: (i: number) => number; y: (v: number) => number; filled?: boolean; step?: boolean; pick?: (p: TrendPoint) => number; color?: string }): React.ReactElement {
  const lastFull = points.length - 1 - (isPartialBucket(points[points.length - 1]?.label ?? "", points) ? 1 : 0);
  const path = (upTo: number, from = 0): string => {
    const slice = points.slice(from, upTo + 1);
    if (step) {
      // Step-after: hold each value flat, then jump at the next point.
      let d = "";
      slice.forEach((p, i) => {
        const px = x(from + i).toFixed(1), py = y(pick(p)).toFixed(1);
        if (i === 0) { d = `M${px},${py}`; }
        else { const prevY = y(pick(slice[i - 1]!)).toFixed(1); d += ` L${px},${prevY} L${px},${py}`; }
      });
      return d;
    }
    return slice
      .map((p, i) => `${i === 0 ? "M" : "L"}${x(from + i).toFixed(1)},${y(pick(p)).toFixed(1)}`)
      .join(" ");
  };
  const areaTo = lastFull;
  const first = points[0];
  // Gradient id derived from color so two overlaid line series never collide on #id.
  const gradId = `tc-grad-${color.replace(/[^a-z0-9]/gi, "")}`;
  const area =
    first !== undefined
      ? `${path(areaTo)} L${x(areaTo).toFixed(1)},${y(0).toFixed(1)} L${x(0).toFixed(1)},${y(0).toFixed(1)} Z`
      : "";
  return (
    <g>
      {filled && (
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.22" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
      )}
      {filled && <path d={area} fill={`url(#${gradId})`} />}
      <path d={path(lastFull)} fill="none" stroke={color} strokeWidth={2.4} strokeLinejoin="round" />
      {lastFull < points.length - 1 && (
        <path d={path(points.length - 1, lastFull)} fill="none" stroke={color} strokeWidth={2} strokeDasharray="4 4" opacity={0.55} />
      )}
    </g>
  );
}

function dot(bg: string): React.CSSProperties {
  return { display: "inline-block", width: 9, height: 9, borderRadius: 3, marginRight: 5, background: bg };
}

/** "Table" chart kind — the same series as plain numbers (newest first), the
 *  current partial bucket flagged. Scrolls if long. */
function TrendTable({ points, fmt, seriesNames }: { points: TrendPoint[]; fmt: (v: number) => string; seriesNames?: [string, string] }): React.ReactElement {
  const twoSeries = points.some((p) => p.b !== undefined);
  const rows = [...points].reverse();
  return (
    <div style={{ maxHeight: 240, overflowY: "auto", border: "1px solid var(--border)", borderRadius: "var(--r-sm)" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
        <thead>
          <tr style={{ position: "sticky", top: 0, background: "var(--surface)" }}>
            <th style={thS("left")}>Period</th>
            <th style={thS("right")}>{twoSeries ? (seriesNames?.[0] ?? "A") : "Value"}</th>
            {twoSeries && <th style={thS("right")}>{seriesNames?.[1] ?? "B"}</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => {
            const partial = isPartialBucket(p.label, points);
            return (
              <tr key={p.label} style={{ opacity: partial ? 0.6 : 1, borderTop: "1px solid var(--border)" }}>
                <td style={tdS("left")}>{prettyMonth(p.label)}{partial && <span style={{ color: "var(--c2)", fontSize: 10 }}> · partial</span>}</td>
                <td style={tdS("right")} className="dd-num">{fmt(p.a)}</td>
                {twoSeries && <td style={tdS("right")} className="dd-num">{p.b !== undefined ? fmt(p.b) : "—"}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** "Number" chart kind — the trend collapsed to its total across full buckets
 *  (the partial current bucket is excluded so it isn't an unfair drag). */
function TrendNumber({ points, fmt, seriesNames }: { points: TrendPoint[]; fmt: (v: number) => string; seriesNames?: [string, string] }): React.ReactElement {
  const full = points.filter((p) => !isPartialBucket(p.label, points));
  const used = full.length > 0 ? full : points;
  const totalA = used.reduce((s, p) => s + p.a, 0);
  const totalB = used.reduce((s, p) => s + (p.b ?? 0), 0);
  const twoSeries = points.some((p) => p.b !== undefined);
  return (
    <div style={{ padding: "22px 4px 14px" }}>
      <div className="dd-num" style={{ fontFamily: "var(--font-display)", fontSize: 34, fontWeight: 700, color: "var(--num)" }}>
        {fmt(twoSeries ? totalA + totalB : totalA)}
      </div>
      <div style={{ color: "var(--body)", fontSize: 12, marginTop: 4 }}>
        total over {used.length} {used.length === 1 ? "period" : "periods"}{full.length < points.length ? " (excl. current partial)" : ""}
      </div>
      {twoSeries && (
        <div style={{ color: "var(--body-faint)", fontSize: 11.5, marginTop: 6 }}>
          <span className="dd-num">{seriesNames?.[0] ?? "A"}: {fmt(totalA)}</span> · <span className="dd-num">{seriesNames?.[1] ?? "B"}: {fmt(totalB)}</span>
        </div>
      )}
    </div>
  );
}

const thS = (a: "left" | "right"): React.CSSProperties => ({ textAlign: a, padding: "6px 10px", fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--faint)", fontWeight: 700 });
const tdS = (a: "left" | "right"): React.CSSProperties => ({ textAlign: a, padding: "6px 10px", color: a === "right" ? "var(--num)" : "var(--body)" });
